import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import {
  UniversalFilterTextPlainValue,
  UniversalFilterTextPlainFilterState,
  UniversalFilterTextPlainOnFilterStateChange,
  UniversalFilterTextPlainOnChange
} from '../../UniversalFilterTextPlain.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterTextPlainFilterState } from '../useUniversalFilterTextPlainFilterState';

interface UniversalFilterTextPlainOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterTextPlainValue;
  onChange: UniversalFilterTextPlainOnChange;
}

function useUniversalFilterTextPlain({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterTextPlainOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterTextPlainFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterTextPlainFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterTextPlainFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          text: ''
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterTextPlainOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleTextChange = useCallback<(text: string) => void>(
    (text) => {
      handleFilterStateChange({
        text: text || ''
      });
    },
    [handleFilterStateChange]
  );

  return {
    filterState,
    handleTextChange
  };
}

export default useUniversalFilterTextPlain;
